import { useState, useCallback, useEffect, useRef } from "react"
import type { ViewMode } from "@/components/layout/document-viewer.types"
import { navDebug } from "@/lib/nav-debug"

export interface NavigationEntry {
  documentId: string | null
  folderPath?: string | null  // For folder navigation in Explorer
  viewMode: ViewMode
  tabType?: string  // Type of tab that created this entry (for back/forward context)
  integrationView?: string  // For integration views (e.g., 'basecamp-todo')
  projectId?: string  // For Basecamp integration
  todoId?: string  // For Basecamp integration
  listId?: string  // For Basecamp todolist tabs
  organizationId?: string  // Workspace org for Basecamp restore
  /** Projects index row selection (Back/Forward restore) */
  selectedEntityId?: string
  /** Projects index secondary preview open (Back/Forward restore) */
  previewOpen?: boolean
  /** Tab title when restoring a non-document surface (inbox thread) */
  title?: string
  /** Keyword query when restoring the advanced search view */
  searchQuery?: string
  /** Path scope when restoring the advanced search view */
  searchPath?: string
  /** Nextcloud path restored by Back/Forward on the Nextcloud tab */
  nextcloudPath?: string
  nextcloudKind?: 'file' | 'folder'
  nextcloudSurface?: 'browse' | 'document'
  nextcloudFileId?: string
  nextcloudPreviewPath?: string
  timestamp: number
}

/**
 * Public navigation API consumed by callers. The instrumented hook
 * (`useInstrumentedNavigation`) returns this exact shape so callsites need not
 * change. The optional `caller` arg on each method tags events in the debug
 * log; callers that omit it still produce events (they just lack a label).
 */
export interface PerTabNavigationApi {
  navigate: (
    tabId: string,
    entry: Omit<NavigationEntry, "timestamp">,
    caller?: string,
  ) => void
  goBack: (tabId: string, caller?: string) => NavigationEntry | null
  goForward: (tabId: string, caller?: string) => NavigationEntry | null
  canGoBack: (tabId: string) => boolean
  canGoForward: (tabId: string) => boolean
  getCurrentEntry: (tabId: string) => NavigationEntry | null
  tabNavigationState: Record<string, {
    history: NavigationEntry[]
    currentIndex: number
  }>
}

// Per-tab navigation history (like Obsidian)
export function usePerTabNavigationHistory() {
  const [tabNavigationState, setTabNavigationState] = useState<Record<string, {
    history: NavigationEntry[];
    currentIndex: number;
  }>>({});

  // Ref kept in sync with state so synchronous loggers (debug) can read the
  // most recent slot snapshot without waiting for a re-render.
  const stateRef = useRef(tabNavigationState);
  stateRef.current = tabNavigationState;

  const snapshotSlot = useCallback((tabId: string) => {
    const cur = stateRef.current[tabId];
    return cur
      ? { historyLen: cur.history.length, index: cur.currentIndex }
      : { historyLen: 0, index: -1 };
  }, []);

  const navigate = useCallback((
    tabId: string,
    entry: Omit<NavigationEntry, "timestamp">,
    caller?: string,
  ) => {
    const newEntry = { ...entry, timestamp: Date.now() };
    const stateBefore = snapshotSlot(tabId);

    // Apply synchronously to stateRef so getCurrentEntry() in the same event tick
    // (e.g. breadcrumb navigate + onFolderClick skip) sees the new stack; React 18
    // batches setState until after handlers return.
    const base = stateRef.current;
    const currentState = base[tabId] || { history: [], currentIndex: -1 };
    const newHistory = currentState.history.slice(0, currentState.currentIndex + 1);
    newHistory.push(newEntry);
    const newIndex = newHistory.length - 1;
    const slotState = { history: newHistory, currentIndex: newIndex };
    const merged = { ...base, [tabId]: slotState };
    stateRef.current = merged;
    setTabNavigationState(merged);

    navDebug.record({
      action: "navigate",
      slotId: tabId,
      caller,
      entry,
      stateBefore,
      stateAfter: {
        historyLen: slotState.history.length,
        index: slotState.currentIndex,
      },
    });
  }, [snapshotSlot]);

  const goBack = useCallback((tabId: string, caller?: string) => {
    const currentState = stateRef.current[tabId] || { history: [], currentIndex: -1 };
    const stateBefore = { historyLen: currentState.history.length, index: currentState.currentIndex };
    if (currentState.currentIndex > 0) {
      const newIndex = currentState.currentIndex - 1;
      const merged = {
        ...stateRef.current,
        [tabId]: {
          ...currentState,
          currentIndex: newIndex
        }
      };
      stateRef.current = merged;
      setTabNavigationState(merged);
      const result = currentState.history[newIndex] || null;
      navDebug.record({
        action: "goBack",
        slotId: tabId,
        caller,
        result,
        stateBefore,
        stateAfter: { historyLen: stateBefore.historyLen, index: newIndex },
      });
      return result;
    }
    navDebug.record({
      action: "goBack",
      slotId: tabId,
      caller,
      result: null,
      stateBefore,
      stateAfter: stateBefore,
    });
    return null;
  }, []);

  const goForward = useCallback((tabId: string, caller?: string) => {
    const currentState = stateRef.current[tabId] || { history: [], currentIndex: -1 };
    const stateBefore = { historyLen: currentState.history.length, index: currentState.currentIndex };
    if (currentState.currentIndex < currentState.history.length - 1) {
      const newIndex = currentState.currentIndex + 1;
      const merged = {
        ...stateRef.current,
        [tabId]: {
          ...currentState,
          currentIndex: newIndex
        }
      };
      stateRef.current = merged;
      setTabNavigationState(merged);
      const result = currentState.history[newIndex] || null;
      navDebug.record({
        action: "goForward",
        slotId: tabId,
        caller,
        result,
        stateBefore,
        stateAfter: { historyLen: stateBefore.historyLen, index: newIndex },
      });
      return result;
    }
    navDebug.record({
      action: "goForward",
      slotId: tabId,
      caller,
      result: null,
      stateBefore,
      stateAfter: stateBefore,
    });
    return null;
  }, []);

  const canGoBack = useCallback((tabId: string) => {
    const currentState = stateRef.current[tabId] || { history: [], currentIndex: -1 };
    return currentState.currentIndex > 0;
  }, []);

  const canGoForward = useCallback((tabId: string) => {
    const currentState = stateRef.current[tabId] || { history: [], currentIndex: -1 };
    return currentState.currentIndex < currentState.history.length - 1;
  }, []);

  const getCurrentEntry = useCallback((tabId: string) => {
    const currentState = stateRef.current[tabId] || { history: [], currentIndex: -1 };
    return currentState.history[currentState.currentIndex] || null;
  }, []);

  // Emit a snapshot event after every commit so the debug panel sees the
  // post-render state (which the synchronous goBack/navigate logs cannot
  // capture due to React's async setState semantics).
  useEffect(() => {
    const fullState: Record<string, { historyLen: number; index: number }> = {};
    for (const [slotId, slot] of Object.entries(tabNavigationState)) {
      fullState[slotId] = { historyLen: slot.history.length, index: slot.currentIndex };
    }
    navDebug.record({
      action: "snapshot",
      slotId: "*",
      fullState,
    });
  }, [tabNavigationState]);

  return { navigate, goBack, goForward, canGoBack, canGoForward, getCurrentEntry, tabNavigationState };
}

// Global navigation history (for backward compatibility)
export function useNavigationHistory() {
  const [history, setHistory] = useState<NavigationEntry[]>([
    { documentId: null, viewMode: "list", timestamp: Date.now() }
  ])
  const [currentIndex, setCurrentIndex] = useState(0)

  const canGoBack = currentIndex > 0
  const canGoForward = currentIndex < history.length - 1

  const navigate = useCallback((entry: Omit<NavigationEntry, "timestamp">) => {
    const newEntry = { ...entry, timestamp: Date.now() }
    
    // Remove any forward history when navigating to a new location
    const newHistory = history.slice(0, currentIndex + 1)
    newHistory.push(newEntry)
    
    setHistory(newHistory)
    setCurrentIndex(newHistory.length - 1)
  }, [history, currentIndex])

  const goBack = useCallback(() => {
    if (canGoBack) {
      setCurrentIndex(currentIndex - 1)
      return history[currentIndex - 1]
    }
    return null
  }, [canGoBack, currentIndex, history])

  const goForward = useCallback(() => {
    if (canGoForward) {
      setCurrentIndex(currentIndex + 1)
      return history[currentIndex + 1]
    }
    return null
  }, [canGoForward, currentIndex, history])

  const getCurrentEntry = useCallback(() => {
    return history[currentIndex] || null
  }, [history, currentIndex])

  return {
    navigate,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    getCurrentEntry,
    history,
    currentIndex,
  }
}

